import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { _getTimeDetails } from './utils';
import { isAfter, isValid, setHours, setMinutes, setSeconds } from 'date-fns';
import Clock from './Clock/Clock';
import classNames from 'classnames';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import { UXPContext } from "@core/context/Context";
import { Dropdown } from '@widget/popups/Dropdown';
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import Input from '../Input';
import FormField from '../FormField';
import Label from '../Label';
/**
 * Helper function to normalize time to today's date for TimeRangePicker
 * This allows comparing two times on the same date
 */
const normalizeTimeToToday = (time, zeroSeconds) => {
    const timeDetails = _getTimeDetails(time, zeroSeconds);
    // Extract time components from the result
    const hours = timeDetails.date.getHours();
    const mins = timeDetails.date.getMinutes();
    const secs = timeDetails.date.getSeconds();
    // Create new date with today's date and the extracted time
    const today = new Date();
    let normalized = setHours(today, hours);
    normalized = setMinutes(normalized, mins);
    normalized = setSeconds(normalized, secs);
    return Object.assign(Object.assign({}, timeDetails), { date: normalized });
};
/**
 *
 * @export
 *
 * This component is used to select a time range.
 *
 * @example
 * ```
 *  <TimeRangePicker
 *      startTime={startDate}
 *      endTime={endDate}
 *      onChange={(s, e) => { setStartDate(s); setEndDate(e) }}
 *  />
 * ```
 */
const TimeRangePicker = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const context = React.useContext(UXPContext);
    // props
    let { title, startTime, endTime, onChange, disableInput, dropdownClassname, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, dropdownMaxWidth, onClear } = props;
    // state
    let [range, setRange] = React.useState({ from: null, to: null });
    let [showPicker, setShowPicker] = React.useState(false);
    let [startValue, setStartValue] = React.useState(null);
    let [endValue, setEndValue] = React.useState(null);
    let [isFocused, setIsFocused] = React.useState(false);
    let [showInput, setShowInput] = React.useState(false);
    const triggerRef = React.useRef(null);
    React.useEffect(() => {
        var _a, _b;
        setStartValue((_a = range === null || range === void 0 ? void 0 : range.from) === null || _a === void 0 ? void 0 : _a.timeString);
        setEndValue((_b = range === null || range === void 0 ? void 0 : range.to) === null || _b === void 0 ? void 0 : _b.timeString);
    }, [range]);
    React.useEffect(() => {
        if (startTime && endTime) {
            let _st = normalizeTimeToToday(startTime, true);
            let _et = normalizeTimeToToday(endTime, true);
            // Don't swap here - just store as-is
            // Swapping only happens when picker closes
            setRange({ from: _st, to: _et });
        }
        else if (startTime) {
            let _st = normalizeTimeToToday(startTime, true);
            setRange({ from: _st, to: null });
        }
        else if (endTime) {
            let _et = normalizeTimeToToday(endTime, true);
            setRange({ from: null, to: _et });
        }
        else {
            setRange({ from: null, to: null });
        }
    }, [startTime, endTime]);
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const handleChange = (date, type) => {
        var _a, _b;
        let _range = Object.assign({}, range);
        let _st = (_a = _range === null || _range === void 0 ? void 0 : _range.from) === null || _a === void 0 ? void 0 : _a.date;
        let _et = (_b = _range === null || _range === void 0 ? void 0 : _range.to) === null || _b === void 0 ? void 0 : _b.date;
        if (type == "from") {
            _st = date;
        }
        else {
            _et = date;
        }
        onChange(_st, _et);
    };
    const closePicker = () => {
        // Read from props (not state) to get latest values
        const hasStart = !!startTime;
        const hasEnd = !!endTime;
        // If both times exist, check if we need to swap (start > end)
        if (hasStart && hasEnd) {
            // Get time objects from props (normalized to same date for comparison)
            const startTimeObj = normalizeTimeToToday(startTime, true);
            const endTimeObj = normalizeTimeToToday(endTime, true);
            if (startTimeObj.date && endTimeObj.date && isAfter(startTimeObj.date, endTimeObj.date)) {
                // Swap: start is after end (pass Date objects)
                onChange(endTimeObj.date, startTimeObj.date);
                // Delay closing to allow state to update
                setTimeout(() => {
                    setShowPicker(false);
                    setShowInput(false);
                    setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
                }, 0);
                return; // Exit early
            }
            // Both times valid, continue to close
        }
        else if (hasStart !== hasEnd) {
            // Only one time is selected (XOR), clear both to prevent invalid state
            onChange(null, null);
        }
        setShowPicker(false);
        setShowInput(false);
        setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    const handleManualInputChange = (val, isStart) => {
        // Allow free-form editing, validation happens on blur
        if (isStart) {
            setStartValue(val);
        }
        else {
            setEndValue(val);
        }
    };
    const handleManualInputBlur = (val, isStart) => {
        var _a, _b, _c, _d;
        // Parse and validate on blur
        try {
            let _timeObj = normalizeTimeToToday(val, true);
            if (isValid(_timeObj.date)) {
                let _sd = isStart ? _timeObj.date : normalizeTimeToToday(startTime, true).date;
                let _ed = isStart ? normalizeTimeToToday(endTime, true).date : _timeObj.date;
                onChange(_sd, _ed);
            }
            else {
                // Invalid input, reset to current valid value
                if (isStart) {
                    setStartValue((_a = range === null || range === void 0 ? void 0 : range.from) === null || _a === void 0 ? void 0 : _a.timeString);
                }
                else {
                    setEndValue((_b = range === null || range === void 0 ? void 0 : range.to) === null || _b === void 0 ? void 0 : _b.timeString);
                }
            }
        }
        catch (e) {
            // Invalid input, reset to current valid value
            if (isStart) {
                setStartValue((_c = range === null || range === void 0 ? void 0 : range.from) === null || _c === void 0 ? void 0 : _c.timeString);
            }
            else {
                setEndValue((_d = range === null || range === void 0 ? void 0 : range.to) === null || _d === void 0 ? void 0 : _d.timeString);
            }
        }
    };
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_timerangepicker__dropdown-content", children: [_jsxs("div", { className: "uxpcore_timerangepicker__header", children: [_jsx("div", { className: "uxpcore_timerangepicker__title", children: showInput ? context.$L('uxp-core.text.enter-time') : title }), _jsx("div", { className: "uxpcore_timerangepicker__toggle-input", onClick: (e) => {
                            e.stopPropagation();
                            setShowInput(!showInput);
                        }, title: showInput ? context.$L('uxp-core.text.show-clock') : context.$L('uxp-core.text.type-time'), children: _jsx(FontAwesomeIconWrapper, { icon: ['far', showInput ? 'clock' : 'keyboard'] }) })] }), showInput && (_jsxs("div", { className: "uxpcore_timerangepicker__input-container", children: [_jsxs(FormField, { noMargin: true, noPadding: true, children: [_jsx(Label, { children: context.$L('uxp-core.text.from') }), _jsx(Input, { type: "text", value: startValue, onChange: (val) => handleManualInputChange(val, true), onBlur: (val) => handleManualInputBlur(val, true), readOnly: disableInput, placeholder: "12:30 PM", fullWidth: true, hideClear: true })] }), _jsxs(FormField, { noMargin: true, noPadding: true, children: [_jsx(Label, { children: context.$L('uxp-core.text.to') }), _jsx(Input, { type: "text", value: endValue, onChange: (val) => handleManualInputChange(val, false), onBlur: (val) => handleManualInputBlur(val, false), readOnly: disableInput, placeholder: "12:30 PM", fullWidth: true, hideClear: true })] })] })), !showInput && (_jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_timerangepicker__clock-section", children: _jsx(Clock, { time: startTime, onChange: date => handleChange(date, "from"), disableInput: disableInput }) }), _jsx("div", { className: "uxpcore_timerangepicker__clock-section", children: _jsx(Clock, { time: endTime, onChange: date => handleChange(date, "to"), disableInput: disableInput }) })] })), _jsx("div", { className: "uxpcore_timerangepicker__footer", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.done'), onClick: closePicker, size: "small" }) })] }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    // Check if we have times selected (both must be present)
    const hasStartTime = (_a = range === null || range === void 0 ? void 0 : range.from) === null || _a === void 0 ? void 0 : _a.timeString;
    const hasEndTime = (_b = range === null || range === void 0 ? void 0 : range.to) === null || _b === void 0 ? void 0 : _b.timeString;
    const hasTimes = hasStartTime && hasEndTime;
    const clearButton = (hasTimes && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(null, null); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    return (_jsxs("div", { className: classNames("uxpcore_timerangepicker", { "uxpcore_input--full-width": fullWidth }), style: containerStyles, children: [_jsxs("div", { ref: triggerRef, className: classNames("uxpcore_input", "uxpcore_input--timerangepicker", stateClass, {
                    "uxpcore_input--has-prefix": true,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": hasTimes,
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, children: [_jsx("div", { className: "uxpcore_input__prefix", children: _jsx(FontAwesomeIconWrapper, { icon: ['far', 'clock'] }) }), _jsx("div", { className: "uxpcore_input__value", children: hasStartTime && hasEndTime ? (
                        // Both times exist
                        _jsxs("div", { className: "uxpcore_timerangepicker__display", children: [_jsx("span", { className: "uxpcore_timerangepicker__time", children: (_c = range === null || range === void 0 ? void 0 : range.from) === null || _c === void 0 ? void 0 : _c.timeString }), _jsx("span", { className: "uxpcore_timerangepicker__separator", children: "-" }), _jsx("span", { className: "uxpcore_timerangepicker__time", children: (_d = range === null || range === void 0 ? void 0 : range.to) === null || _d === void 0 ? void 0 : _d.timeString })] })) : !hasStartTime && !hasEndTime ? (
                        // Both empty
                        context.$L('uxp-core.text.select-a-time-range')) : !hasStartTime ? (
                        // Only start missing
                        _jsxs("div", { className: "uxpcore_timerangepicker__display", children: [_jsx("span", { className: "uxpcore_timerangepicker__time", children: context.$L('uxp-core.text.select-a-time-range') }), _jsx("span", { className: "uxpcore_timerangepicker__separator", children: "-" }), _jsx("span", { className: "uxpcore_timerangepicker__time", children: (_e = range === null || range === void 0 ? void 0 : range.to) === null || _e === void 0 ? void 0 : _e.timeString })] })) : (
                        // Only end missing
                        _jsxs("div", { className: "uxpcore_timerangepicker__display", children: [_jsx("span", { className: "uxpcore_timerangepicker__time", children: (_f = range === null || range === void 0 ? void 0 : range.from) === null || _f === void 0 ? void 0 : _f.timeString }), _jsx("span", { className: "uxpcore_timerangepicker__separator", children: "-" }), _jsx("span", { className: "uxpcore_timerangepicker__time", children: context.$L('uxp-core.text.select-a-time') })] })) }), _jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] })] }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_timerangepicker__dropdown", dropdownClassname), matchTriggerWidth: false, maxWidth: dropdownMaxWidth || "15rem", minWidth: "15rem" })] }));
};
export default TimeRangePicker;
